import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Heading from '@atlaskit/heading';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { CollapsibleToggleButton } from '@atlassian/jira-software-releases-collapsible-ui-toggle/src/ui/index.tsx';
import { Section, Summary } from '../../common/ui/styled.tsx';
import type { issuesHeading_versionProgress$key } from './__generated__/issuesHeading_versionProgress.graphql';
import messages from './messages.tsx';

export type Props = {
	jiraVersion: issuesHeading_versionProgress$key;
	isCollapsed: boolean;
	onToggleClicked: () => void;
};

export const IssuesHeading = ({
	jiraVersion,
	isCollapsed,
	onToggleClicked,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issuesHeading_versionProgress on JiraVersion {
				doneIssuesRedesignTemp: issues(filters: { statusCategories: [DONE] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
				inProgressIssuesRedesignTemp: issues(filters: { statusCategories: [IN_PROGRESS] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
				todoIssuesRedesignTemp: issues(filters: { statusCategories: [TODO] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
			}
		`,
		jiraVersion,
	);

	const inProgressIssueCount = version.inProgressIssuesRedesignTemp.totalCount;
	const doneIssueCount = version.doneIssuesRedesignTemp.totalCount;
	const todoIssueCount = version.todoIssuesRedesignTemp.totalCount;
	const totalIssueCount = inProgressIssueCount + doneIssueCount + todoIssueCount;

	return (
		<Section onClick={onToggleClicked} isCollapsible>
			<Heading size="xsmall" as="h3">
				{formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.issuesHeadingIssueTerminologyRefreshM4
						: messages.issuesHeading,
				)}
			</Heading>
			<Summary>
				{totalIssueCount === 0
					? formatMessage(
							fg('jira-issue-terminology-update-m4')
								? messages.noIssuesIssueTerminologyRefreshM4
								: messages.noIssues,
						)
					: formatMessage(messages.doneIssues, {
							doneCount: doneIssueCount,
							totalCount: totalIssueCount,
						})}
				<CollapsibleToggleButton isCollapsed={isCollapsed} />
			</Summary>
		</Section>
	);
};
